import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { alphanumericValidator } from 'avn/modules/shared/directives/alphanumeric.validator';
import moment from 'moment';
import { Subject, BehaviorSubject } from 'rxjs';

export const TIMER_DURATION = 60;
export const TEMP_APPOINTMENT_DURATION = 300;
export const ASSISTED_BOOKING_SESSION_KEY = 'assisted_booking_flow';

const MIN_NAME_LENGTH = 2;
const PROGRESS_CIRCLE_RADIUS = 45;
const FULL_PERCENTAGE = 100;
const MS_PER_SECOND = 1000;

interface IActivePolicy {
  active: boolean;
  version: string;
  link: string;
}

@Injectable({
  providedIn: 'root'
})
export class BookingStoreService {
  public bookingCompleteStatusList: any = {
    service: true,
    practitioner: false,
    datetime: false,
    customer: false,
    confirm: false
  };

  public isRecaptchaValid = '';

  public startTimerSub = new Subject();

  public bookingLayout: 'webpage' | 'mobile' = 'webpage';

  public isAppointmentOrClass: 'Appointment' | 'Classes' = 'Appointment';

  public bookingTemporarySlug!: string;

  public currentFlow: 'appointment' | 'time' | 'customer' | 'checkout' = 'appointment';

  public showPop: '' | 'practitioners' | 'service_type' | 'validate_email' | 'mobile_address' = '';

  public servicePractitioners: any[] = [];

  public loggedUser: any;

  public businessDetail: any = {};
  public businessSlug = '';

  public serviceParam = '';
  public serviceUuid = '';
  public selectedServiceId!: number;
  public selectedServiceDetail: any = {};

  public serviceDetail: any = {};
  public serviceListObj: any = {};

  public practitionerId: string | number = '';
  public practitionerDetail: any = {};

  public selectedDate = '';
  public selectedTime = '';

  public apiLoading = false;

  public appointmentType!: '' | 'In Person' | 'Online' | 'Mobile';

  public getComponentRelatedCalls = new Subject();

  public wellbeingCreditApplied = 0;

  public customerForm: FormGroup = this.fb.group({
    claim_number: ['', [Validators.required, alphanumericValidator]],
    customer_name: ['', [Validators.required, Validators.minLength(MIN_NAME_LENGTH), alphanumericValidator]],
    customer_last_name: ['', [Validators.required, Validators.minLength(MIN_NAME_LENGTH), alphanumericValidator]],
    customer_email: ['', [Validators.required, Validators.email]],
    customer_dob: ['', [Validators.required]],
    customer_phone: ['', [Validators.required]],
    country_code: ['+61', [Validators.required]],
    appointment_notes: ['']
  });

  public phoneObject: { phone_number: string; country_code: string } = { phone_number: '', country_code: '+61' };

  public tempApptResponse: any;
  public serviceResponse: any;

  // CHECKOUT RELATED VARIABLES
  public promoCode!: string;
  public promoApplied = false;
  public promoValidated: any = {};

  public paymentMode: '' | 'venue' | 'creditcard' | 'helloclever' | 'wellbeingcredit' = '';

  // Credit card valid flags
  public cardNumberValid = false;
  public cardExpiryValid = false;
  public cardCvvValid = false;
  public stripePayment: any;
  public stripeCardNumberElement: any;
  public stripeCardExpElement: any;
  public stripeCardCVVElement: any;

  // Pay at venue OTP
  public venueOtp = '';

  public formattedAppointmentTime = '';
  public progressPercentage = FULL_PERCENTAGE;

  public readonly circumference = 2 * Math.PI * PROGRESS_CIRCLE_RADIUS;
  public strokeDashoffset: number = this.circumference;

  public isVenue = false;
  public isOnline = false;
  public isMobile = false;

  public bTMobileAddress = {
    address: '',
    apartment: '',
    suburb: '',
    state: '',
    post_code: ''
  };

  public isChecked = false;
  public isPolicyChecked = false;
  public isReportChecked = false;
  public policiesApiLoading = false;

  public activePolicies: { tc: IActivePolicy; privacy: IActivePolicy; health: IActivePolicy } = {
    tc: { active: false, version: 'v0.0', link: '' },
    privacy: { active: false, version: 'v0.0', link: '' },
    health: { active: false, version: 'v0.0', link: '' }
  };

  public emailExists = false;

  public bookingCompletedData = {
    id: 0,
    appointment_start_time: '',
    appointment_end_time: '',
    appointment_date: '',
    practitioner_detail: {
      name: '',
      profession_title: '',
      correct_image: ''
    },
    business_detail: {
      name: '',
      business_location: {}
    },
    appointment_number: '',
    business_address: '',
    street: '',
    suburb: '',
    state: '',
    postal_code: '',
    claim_number: '',
    customer_name: '',
    customer_last_name: '',
    customer_email: '',
    booking_type: ''
  };

  public get isAssistedBooking(): boolean {
    // sessionStorage is unavailable during SSR
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    return sessionStorage.getItem(ASSISTED_BOOKING_SESSION_KEY) === 'true';
  }

  private readonly _selectedPractitionerLocation$ = new BehaviorSubject<any>({});

  public selectedPractitionerLocation$ = this._selectedPractitionerLocation$.asObservable();

  public constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router
  ) {
    this.setupNameCapitalization();
  }

  public setActivePolicies(response: any): void {
    const policies = response || {};

    this.activePolicies = {
      tc: {
        active: !!policies.tc_link,
        version: policies.tc_version || '',
        link: policies.tc_link || ''
      },
      privacy: {
        active: !!policies.privacy_link,
        version: policies.privacy_version || '',
        link: policies.privacy_link || ''
      },
      health: {
        active: !!policies.health_link,
        version: policies.health_version || '',
        link: policies.health_link || ''
      }
    };
  }

  public initSelectedPractitionerLocationFromService(): void {
    const location = this.serviceDetail?.business_data?.business_location || {};
    this._selectedPractitionerLocation$.next(location);
  }

  public setSelectedPractitionerLocation(location: any): void {
    this._selectedPractitionerLocation$.next(location);
  }

  public getSelectedPractitionerLocation(): any {
    return this._selectedPractitionerLocation$.getValue();
  }

  public capitalizeNameField(controlName: 'customer_name' | 'customer_last_name', value: string): void {
    const control = this.customerForm.get(controlName);
    if (!control || typeof value !== 'string' || !value.length) {
      return;
    }
    const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
    if (capitalized !== value) {
      control.setValue(capitalized, { emitEvent: false });
    }
  }

  public checkIfMoreThanOneActive(): boolean {
    return [this.isVenue, this.isOnline, this.isMobile].filter(Boolean).length > 1;
  }

  public checkIsAppointTypeAvailable(): void {
    this.isOnline =
      this.serviceDetail?.business_data?.business_detail?.online_service &&
      this.selectedServiceDetail?.mode_of_service?.indexOf('Online') > -1 &&
      this.practitionerDetail.logic_health_online;
    this.isMobile =
      this.serviceDetail?.business_data?.business_detail?.mobile_service &&
      this.selectedServiceDetail?.mode_of_service?.indexOf('Mobile') > -1 &&
      this.practitionerDetail.logic_health_mobile;
    this.isVenue =
      this?.selectedServiceDetail?.mode_of_service?.indexOf('In Person') > -1 &&
      this.practitionerDetail.logic_health_partner;
  }

  public resetBookingData(): void {
    this.bookingCompleteStatusList = {
      service: true,
      practitioner: false,
      datetime: false,
      customer: false,
      confirm: false
    };
    this.isAppointmentOrClass = 'Appointment';
    this.bookingTemporarySlug = '';
    this.currentFlow = 'appointment';
    this.businessDetail = {};
    this.businessSlug = '';
    this.serviceParam = '';
    this.serviceUuid = '';
    this.selectedServiceId = 0;
    this.selectedServiceDetail = {};
    this.serviceDetail = {};
    this.serviceListObj = {};
    this.practitionerId = 0;
    this.practitionerDetail = {};
    this.selectedDate = '';
    this.selectedTime = '';
    this.appointmentType = '';
    this.wellbeingCreditApplied = 0;
    this.tempApptResponse = {};
    this.serviceResponse = {};
    this.promoCode = '';
    this.promoApplied = false;
    this.promoValidated = false;
    this.paymentMode = '';
    this.cardNumberValid = false;
    this.cardExpiryValid = false;
    this.cardCvvValid = false;
    this.venueOtp = '';
    this.formattedAppointmentTime = '';
    this.progressPercentage = FULL_PERCENTAGE;
    this.emailExists = false;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('app-duration');
    }
    this.bookingCompletedData = {
      id: 0,
      appointment_start_time: '',
      appointment_end_time: '',
      appointment_date: '',
      practitioner_detail: {
        name: '',
        profession_title: '',
        correct_image: ''
      },
      business_detail: {
        name: '',
        business_location: {}
      },
      appointment_number: '',
      business_address: '',
      street: '',
      suburb: '',
      state: '',
      postal_code: '',
      claim_number: '',
      customer_name: '',
      customer_last_name: '',
      customer_email: '',
      booking_type: ''
    };
    this.customerForm.reset();
  }

  public updateCompleteStatus(): void {
    this.bookingCompleteStatusList = {
      service: !!(this.bookingTemporarySlug || this.serviceParam),
      practitioner: !!(this.bookingTemporarySlug || this.practitionerId),
      datetime: !!(this.bookingTemporarySlug || (this.selectedDate && this.selectedTime)),
      customer: !!this.bookingTemporarySlug,
      confirm: false
    };
  }

  public updateFormattedCountdown(time: any): string {
    return moment(time * MS_PER_SECOND)
      .utc()
      .format('mm:ss');
  }

  public updateProgressBar(timeleft: any, duration: any): number {
    return (timeleft / duration) * FULL_PERCENTAGE;
  }

  public fetchProviderDiscount(): number {
    if (this.selectedServiceDetail.discount > 0) {
      return this.selectedServiceDetail.price - this.selectedServiceDetail.discount;
    }
    return 0;
  }

  public fetchPromoDiscount(): number {
    if (this.promoApplied) {
      if (this.selectedServiceDetail.discount > 0) {
        const disc = (this.selectedServiceDetail.discount * this.promoValidated.percent) / FULL_PERCENTAGE;
        return disc;
      } else {
        const disc = (this.selectedServiceDetail.price * this.promoValidated.percent) / FULL_PERCENTAGE;
        return disc;
      }
    } else {
      return 0;
    }
  }

  public computeFinalPrice(): number {
    let price = this.selectedServiceDetail?.price || 0;
    if (this.selectedServiceDetail?.discount && this.selectedServiceDetail.discount > 0) {
      price = this.selectedServiceDetail.discount;
    }
    if (this.promoApplied) {
      const disc = (price * this.promoValidated.percent) / FULL_PERCENTAGE;
      price = price - disc;
    }
    if (this.wellbeingCreditApplied > 0) {
      price = price - this.wellbeingCreditApplied;
    }
    return price;
  }

  public generateEndTime(): string {
    let extend: string = this.selectedServiceDetail.length_of_service || '';
    let hoursToExtend = 0;
    let minutesToExtend = 0;

    if (!isNaN(Number(extend))) {
      extend += ' mins';
    }

    const hoursToCheck = extend.toLowerCase().split('hr');
    if (!isNaN(Number(hoursToCheck[0]))) {
      hoursToExtend = Number(hoursToCheck[0]);
      extend = hoursToCheck[1];
    }
    const minutesToCheck = extend.toLowerCase().split('mins');
    if (!isNaN(Number(minutesToCheck[0]))) {
      minutesToExtend = Number(minutesToCheck[0]);
    }

    return moment(this.selectedTime, 'hh:mmA')
      .add(minutesToExtend, 'minutes')
      .add(hoursToExtend, 'hours')
      .format('hh:mm A');
  }

  public goToPractitionerPage(id: string | number): void {
    let url = `booking/${this.businessSlug}/${this.serviceParam}/${String(id)}`;
    if (this.selectedDate) {
      url += `/${this.selectedDate}`;
    }
    if (this.selectedTime) {
      url += `/${this.selectedTime}/${this.currentFlow}`;
    }

    let queryParams: any = {};
    queryParams = this.parseQueryString(sessionStorage.getItem('path-query') || '');

    this.router.navigate([url], {
      queryParams: queryParams
    });
  }

  public goToTimePage(): void {
    let url = `booking/${this.businessSlug}/${String(this.selectedServiceDetail.uuid)}${
      this.isAppointmentOrClass === 'Appointment' ? 'a' : 'c'
    }${this.getBookingTypeUrlExt()}/${String(this.practitionerId)}`;
    if (this.bookingLayout === 'mobile') {
      url += '/time';
    }

    let queryParams: any = {};
    queryParams = this.parseQueryString(sessionStorage.getItem('path-query') || '');

    this.router.navigate([url], {
      queryParams: queryParams
    });
  }

  public changeAppointmentType(appointType: 'In Person' | 'Online' | 'Mobile'): void {
    this.appointmentType = appointType;
  }

  public getBookingTypeUrlExt(): string {
    switch (this.appointmentType) {
      case 'In Person':
        return 'p';
      case 'Mobile':
        return 'm';
      case 'Online':
        return 'o';
      default:
        return '-';
    }
  }

  public getBookingTypeExt(ext: '-' | 'p' | 'o' | 'm'): '' | 'In Person' | 'Online' | 'Mobile' {
    switch (ext) {
      case 'p':
        return 'In Person';
      case 'm':
        return 'Mobile';
      case 'o':
        return 'Online';
      default:
        return '';
    }
  }

  public parseQueryString(query: string): Record<string, string | number> {
    if (query.includes('?')) {
      query = query.split('?')[1];
    }
    const params = new URLSearchParams(query);
    const result: Record<string, string | number> = {};

    params.forEach((value, key) => {
      // Convert numeric strings to actual numbers if possible
      const numberValue = Number(value);
      result[key] = isNaN(numberValue) ? value : numberValue;
    });

    return result;
  }

  private setupNameCapitalization(): void {
    const capitalizeControl = (controlName: 'customer_name' | 'customer_last_name'): void => {
      const control = this.customerForm.get(controlName);
      control?.valueChanges.subscribe((value: string) => {
        if (typeof value !== 'string' || !value.length) {
          return;
        }
        const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
        if (capitalized !== value) {
          control.setValue(capitalized, { emitEvent: false });
        }
      });
    };

    capitalizeControl('customer_name');
    capitalizeControl('customer_last_name');
  }
}
